import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEO, buildFAQLD } from "../components/SEO";
import { Search, ChevronRight } from "lucide-react";
import { Input } from "../components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi, { buildWhatsAppUrl, DEFAULT_CONTACT_PHONE } from "../services/odooApi";

export const FAQPage = () => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [cmsFaqs, setCmsFaqs] = useState([]);
  const [whatsappNumber, setWhatsAppNumber] = useState(DEFAULT_CONTACT_PHONE);
  const staticCategories = [
    {
      name: t("faq.category.bookingReservations", "Booking & Reservations"),
      key: "booking",
      faqs: [
        {
          question: t("faq.question.howBook", "How do I book a car?"),
          answer: t(
            "faq.answer.howBook",
            "Booking a car is easy! Simply browse our collection, select your preferred vehicle, and contact us via WhatsApp, phone, or the inquiry form.",
          ),
        },
        {
          question: t("faq.question.advanceBooking", "How far in advance should I book?"),
          answer: t(
            "faq.answer.advanceBooking",
            "We recommend booking at least 24-48 hours in advance, especially for luxury vehicles.",
          ),
        },
      ],
    },
  ];

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const [data, config] = await Promise.all([
          odooApi.getFaqs(),
          odooApi.getCmsConfig(),
        ]);
        if (data && data.length > 0) setCmsFaqs(data);
        setWhatsAppNumber(config?.whatsapp || config?.phone || DEFAULT_CONTACT_PHONE);
      } catch (e) {
        // fallback to static
      }
    };
    loadFaqs();
  }, []);

  const whatsappUrl = buildWhatsAppUrl(
    whatsappNumber,
    t("contact.whatsappGeneralPrefill", "Hello! I'm interested in renting a car from Velaris Royal."),
    '',
  );

  // Group CMS FAQs by category or use static fallback
  const faqCategories = cmsFaqs.length > 0
    ? groupFaqsByCategory(cmsFaqs, t)
    : staticCategories;
  const faqs = faqCategories.flatMap((category) => category.faqs);

  const filteredCategories = faqCategories.map((category) => ({
    ...category,
    faqs: category.faqs.filter(
      (faq) =>
        searchQuery === "" ||
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => 
    selectedCategory === "all" || category.name === selectedCategory
  );

  return (
    <>
      <SEO
        title={t("faq.metaTitle", "FAQ | Frequently Asked Questions About Car Rental")}
        description="Frequently asked questions about renting cars from Velaris Royal. Find answers about booking, requirements, pricing, insurance, and more."
        keywords="car rental faq, car hire questions dubai, rental requirements uae, luxury car rental terms"
        structuredData={faqs.length > 0 ? buildFAQLD(faqs) : undefined}
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("faq.title", "FAQ") },
        ]}
      />

      <div className="min-h-screen bg-white" data-testid="faq-page">
        {/* Hero */}
        <div className="bg-black py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-block bg-gold-gradient text-black px-4 py-1.5 text-xs uppercase tracking-[0.2em] font-semibold mb-6">
              {t("faq.helpCenter", "Help Center")}
            </span>
            <h1 className="font-[var(--font-display)] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t("faq.heading", "Frequently Asked Questions")}
            </h1>
            <p className="text-white/70 max-w-xl mx-auto mb-8">
              {t("faq.subtitle", "Find answers to common questions about our car rental services.")}
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("faq.searchPlaceholder", "Search for answers...")}
                className="pl-12 py-6 text-lg bg-white/10 border-white/20 text-white placeholder:text-white/50"
                data-testid="faq-search"
              />
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="border-b bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex overflow-x-auto py-4 gap-4 scrollbar-hide">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === "all"
                    ? "bg-[#D4AF37] text-black"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
                data-testid="faq-category-all"
              >
                {t("faq.allQuestions", "All Questions")}
              </button>
              {faqCategories.map((category) => (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === category.name
                      ? "bg-[#D4AF37] text-black"
                      : "bg-white text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {filteredCategories.map((category) => (
            category.faqs.length > 0 && (
              <div key={category.name} className="mb-12">
                <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 mb-6">
                  {category.name}
                </h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.faqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`${category.name}-${index}`}
                      className="border border-gray-100 rounded-lg px-6 data-[state=open]:border-[#D4AF37]"
                    >
                      <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-[#D4AF37] py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 pb-4 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )
          ))}

          {filteredCategories.every((cat) => cat.faqs.length === 0) && (
            <div className="text-center py-16">
              <p className="text-gray-500 mb-4">{t("faq.noMatch", "No questions found matching your search.")}</p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
                className="text-[#D4AF37] hover:text-[#C5A028] font-medium"
              >
                {t("faq.clearSearch", "Clear search")}
              </button>
            </div>
          )}
        </div>

        {/* Still Have Questions */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 mb-4">
              {t("faq.stillHaveQuestions", "Still Have Questions?")}
            </h2>
            <p className="text-gray-600 mb-8">
              {t("faq.supportDescription", "Can't find what you're looking for? Our support team is here to help.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {whatsappUrl !== '#' && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => odooApi.trackClick({ click_type: "whatsapp" })}
                  className="bg-[#25D366] text-white px-8 py-3 rounded-sm font-semibold hover:bg-[#1da851] transition-colors"
                >
                  {t("faq.chatWhatsApp", "Chat on WhatsApp")}
                </a>
              )}
              <Link
                to="/contact"
                className="bg-black text-white px-8 py-3 rounded-sm font-semibold hover:bg-gray-800 transition-colors inline-flex items-center justify-center"
              >
                {t("btn.contactUs", "Contact Us")}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQPage;

function groupFaqsByCategory(faqs, t) {
  const CATEGORY_LABELS = {
    general: t("faq.category.general", "General"),
    booking: t("faq.category.booking", "Booking"),
    payment: t("faq.category.payment", "Payment"),
    insurance: t("faq.category.insurance", "Insurance"),
    documents: t("faq.category.documents", "Documents"),
  };
  const groups = {};
  for (const faq of faqs) {
    const cat = faq.category || 'general';
    const label = CATEGORY_LABELS[cat] || cat;
    if (!groups[label]) groups[label] = [];
    groups[label].push(faq);
  }
  return Object.entries(groups).map(([name, faqs]) => ({ name, faqs }));
}

